import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import RoomModal from './RoomModal';
import BatchUploadModal from './BatchUploadModal';
import { roomService } from '../../../services/roomService';
import { hierarchyService } from '../../../services/hierarchyService';
import ActionToast from '../../../components/ui/ActionToast';
import RoomTypeList from './RoomTypeList';

const RoomManagement = () => {
    const [activeTab, setActiveTab] = useState('rooms');
    const [rooms, setRooms] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [minimumCapacity, setMinimumCapacity] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const [showRoomModal, setShowRoomModal] = useState(false);
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [roomsResult, departmentsResult, roomTypesResult] = await Promise.allSettled([
                roomService.getAll(),
                hierarchyService.getDepartments(),
                roomService.getRoomTypes()
            ]);
            if (roomsResult.status === 'rejected') throw roomsResult.reason;
            setRooms(roomsResult.value || []);
            if (departmentsResult.status === 'fulfilled') setDepartments(departmentsResult.value || []);
            else console.warn('Department filters could not be loaded:', departmentsResult.reason);
            if (roomTypesResult.status === 'fulfilled') setRoomTypes(roomTypesResult.value || []);
        } catch (err) {
            console.error("Failed to load room data", err);
            setError(err?.message || 'Rooms could not be loaded.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRoom = () => {
        setSelectedRoom(null);
        setShowRoomModal(true);
    };

    const handleEditRoom = (room) => {
        setSelectedRoom(room);
        setShowRoomModal(true);
    };

    const handleSaveRoom = async (roomData) => {
        try {
            if (selectedRoom) await roomService.update(selectedRoom.id, roomData);
            else await roomService.create(roomData);
            await fetchData();
            setNotice({ type: 'success', message: selectedRoom ? `${roomData.name} was updated successfully.` : `${roomData.name} was created successfully.` });
        } catch (saveError) {
            setNotice({ type: 'error', message: saveError?.message || 'The room could not be saved.' });
            throw saveError;
        }
    };

    const handleBatchUpload = async (batchData) => {
        await roomService.batchCreate(batchData);
        fetchData();
    };

    const handleDeleteRoom = async (id) => {
        if (window.confirm('Are you sure you want to delete this room?')) {
            await roomService.delete(id);
            fetchData();
        }
    };

    const filteredRooms = rooms.filter(room => {
        const matchSearch = String(room.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(room.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(room.type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(room.location || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchDept = selectedDepartment === 'all' || room.departmentId === selectedDepartment;
        const roomTypeRecord = roomTypes.find(type => type.name === room.type);
        const matchCategory = selectedCategory === 'all' || roomTypeRecord?.category === selectedCategory;
        const matchCapacity = !minimumCapacity || Number(room.capacity || 0) >= Number(minimumCapacity);
        const matchStatus = selectedStatus === 'all' || String(room.status || '').toLowerCase() === selectedStatus;
        return matchSearch && matchDept && matchCategory && matchCapacity && matchStatus;
    });
    const categories = [...new Set(roomTypes.map(type => type.category).filter(Boolean))];
    const hasFilters = Boolean(searchTerm || selectedDepartment !== 'all' || selectedCategory !== 'all' || minimumCapacity || selectedStatus !== 'all');
    const resetFilters = () => { setSearchTerm(''); setSelectedDepartment('all'); setSelectedCategory('all'); setMinimumCapacity(''); setSelectedStatus('all'); };

    return (
        <div className="space-y-6">
            <ActionToast notice={notice} onClose={() => setNotice(null)} />
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 border-b border-border pb-4">
                <div className="flex space-x-6">
                    <button 
                        className={`pb-4 -mb-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'rooms' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setActiveTab('rooms')}
                    >
                        Rooms
                    </button>
                    <button 
                        className={`pb-4 -mb-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'types' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setActiveTab('types')}
                    >
                        Room Types
                    </button>
                </div>
                {activeTab === 'rooms' && (
                    <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm" onClick={() => setShowBatchModal(true)}>
                            <Icon name="Upload" size={14} className="mr-1" />
                            Batch Upload
                        </Button>
                        <Button variant="default" size="sm" onClick={handleCreateRoom}>
                            <Icon name="Plus" size={14} className="mr-1" />
                            Add Room
                        </Button>
                    </div>
                )}
            </div>

            {activeTab === 'rooms' && (
                <>
                    <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex flex-wrap items-center gap-2 pb-2">
                    <div className="w-full sm:w-52"><Input placeholder="Search rooms..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
                    {departments.length > 1 && <select
                        className="h-10 w-full sm:w-44 px-3 border border-border rounded-md bg-input text-sm text-foreground"
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                    >
                        <option value="all">All Departments</option>
                        {departments.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>}
                    <select className="h-10 w-full sm:w-48 px-3 border border-border rounded-md bg-input text-sm text-foreground" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}><option value="all">All categories</option>{categories.map(category => <option key={category} value={category}>{category}</option>)}</select>
                    <div className="w-full sm:w-36"><Input type="number" min="1" placeholder="Min. capacity" value={minimumCapacity} onChange={e => setMinimumCapacity(e.target.value)} /></div>
                    <select className="h-10 w-full sm:w-36 px-3 border border-border rounded-md bg-input text-sm text-foreground" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="maintenance">Maintenance</option></select>
                    <Button type="button" variant="outline" size="sm" className="h-10 w-full sm:w-auto" disabled={!hasFilters} onClick={resetFilters}><Icon name="RotateCcw" size={14} className="mr-2" />Reset</Button>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">Showing <b className="text-foreground">{filteredRooms.length}</b> of <b className="text-foreground">{rooms.length}</b> rooms</div>
            </div>

            {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800"><p>{error}</p><Button variant="outline" size="sm" className="mt-3" onClick={fetchData}><Icon name="RefreshCw" size={14} className="mr-2" />Retry</Button></div>}

            {/* Desktop Table View - "Ruled Book" Style */}
            {!error &&
            <div className="flex-1 rounded-md border border-blue-200 overflow-hidden flex flex-col bg-background shadow-sm h-[calc(100vh-280px)]">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-sm">
                        <thead className="bg-white text-muted-foreground sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Room ID</th>
                                <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Name</th>
                                <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Department</th>
                                <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Type</th>
                                <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Capacity</th>
                                <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Status</th>
                                <th className="px-4 py-3 text-right font-medium border-b-2 border-blue-100">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-blue-100">
                            {loading && <tr><td colSpan="7" className="px-4 py-12 text-center text-muted-foreground"><Icon name="Loader" size={24} className="mx-auto mb-2 animate-spin" />Loading rooms…</td></tr>}
                            {!loading && <>
                            {filteredRooms.map((room) => (
                                <tr key={room.id} className="hover:bg-blue-50/50 transition-colors group">
                                    <td className="px-4 py-3 border-b border-blue-100 font-mono text-xs text-blue-800">{room.id}</td>
                                    <td className="px-4 py-3 border-b border-blue-100 font-medium text-gray-800">{room.name}</td>
                                    <td className="px-4 py-3 border-b border-blue-100 text-gray-600">{room.departmentName}</td>
                                    <td className="px-4 py-3 border-b border-blue-100 text-gray-700">{room.type}</td>
                                    <td className="px-4 py-3 border-b border-blue-100 text-gray-700">{room.capacity}</td>
                                    <td className="px-4 py-3 border-b border-blue-100">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${room.status === 'Active' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                            {room.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 border-b border-blue-100 text-right">
                                        <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button variant="ghost" size="icon" onClick={() => handleEditRoom(room)} title="Edit">
                                                <Icon name="Edit" size={14} className="text-muted-foreground hover:text-foreground" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="text-error hover:text-error hover:bg-error/10" onClick={() => handleDeleteRoom(room.id)} title="Delete">
                                                <Icon name="Trash2" size={14} />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredRooms.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="px-4 py-12 text-center text-muted-foreground">
                                        No rooms found matching your criteria.
                                    </td>
                                </tr>
                            )}
                            </>}
                        </tbody>
                    </table>
                </div>
            </div>}

            <RoomModal
                isOpen={showRoomModal}
                onClose={() => setShowRoomModal(false)}
                onSave={handleSaveRoom}
                room={selectedRoom}
                fixedDepartment={departments.length === 1 ? departments[0] : null}
            />

            <BatchUploadModal
                isOpen={showBatchModal}
                onClose={() => setShowBatchModal(false)}
                onUpload={handleBatchUpload}
            />
                </>
            )}

            {activeTab === 'types' && (
                <RoomTypeList />
            )}
        </div>
    );
};

export default RoomManagement;
